import { Children, cloneElement, useEffect, useState } from 'react'
import { Wizard } from '../Wizard/Wizard'
import { MultiStepWizardProps, StepProps } from './Types'
import { Step } from './Step'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'
import { useModals } from 'almanac-ui/Modal'
import { SaveDraftModal } from './SaveDraftModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { canSaveDraft } from './utils'
import { useHistory } from 'react-router-dom'

const Root = ({
  schema,
  onSubmit,
  initialValues = {},
  initialFormStatus,
  children,
  onClose,
  initialStep = 0,
  initialCompletedAll = false,
  syncHashWithStep = false,
}: MultiStepWizardProps) => {
  const {
    meta: { title, icon, previewUrl, type },
    steps,
  } = schema

  const { openModal, closeModal } = useModals()
  const savedDraftsOn = useFlipperFlag('saved_drafts')
  const history = useHistory()

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [values, setValues] = useState(initialValues)
  const [completedAll, setCompletedAll] = useState(initialCompletedAll)

  useEffect(() => {
    if (!syncHashWithStep) return
    history.replace({ hash: `#${currentStep}` })
  }, [syncHashWithStep, currentStep, history])

  const handleSubmit = async (partialValues: object) => {
    if (currentStep === steps.length - 1) {
      setCompletedAll(true)
      return onSubmit({ ...values, ...partialValues })
    }
    setCurrentStep(currentStep + 1)
    setValues((v: any) => ({ ...v, ...partialValues }))
  }

  const handleClose = () => {
    if (currentStep > 0 && savedDraftsOn && canSaveDraft(schema.meta.type)) {
      openModal(
        SaveDraftModal,
        { values, currentStep, completedAll, onClose, channel: schema.meta.type },
        { newStack: true },
      )
    } else {
      onClose?.()
      closeModal()
    }
  }

  const childrenArray = Children.toArray(children)
  const hiddenStepIndexes = steps
    .filter((step) => !!step.describe().meta.hideStepHeader)
    .map((step) => steps.indexOf(step) + 1)

  const allowBack = steps[currentStep].describe().meta.allowBack
  const allowClose = steps[currentStep].describe().meta.allowClose

  return (
    <WizardProvider schema={schema}>
      <Wizard.Root
        aria-label={title}
        aria-labelledby="wizard-header"
        type={type}
        onClose={handleClose}
        closeModalOnCancel={false}
      >
        <ResponsiveDesign.Mobile>
          <Wizard.MobileHeader
            steps={steps.map((step) => step.describe().meta.title)}
            currentStep={currentStep + 1}
            onStepPress={(s) => setCurrentStep(s - 1)}
            title={title}
            icon={icon}
            completedAll={completedAll}
          />
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <Wizard.Header
            previewUrl={previewUrl}
            steps={steps.map((step) => step.describe().meta.title)}
            hiddenStepIndexes={hiddenStepIndexes}
            currentStep={currentStep + 1}
            onClose={onClose}
            onStepPress={allowBack ? (s) => setCurrentStep(s - 1) : undefined}
            title={title}
            icon={icon}
            completedAll={completedAll}
            withCloseButton={allowClose}
          />
        </ResponsiveDesign.Desktop>
        {childrenArray.length > 0 && (
          <div aria-current="step" data-testid={`wizard-step-${currentStep + 1}`} style={{ width: '100%' }}>
            {cloneElement(childrenArray[currentStep] as React.ReactElement<StepProps>, {
              initialValues: {
                ...values,
                ...(childrenArray[currentStep] as React.ReactElement<StepProps>).props.initialValues,
              },
              initialFormStatus,
              schema: steps[currentStep],
              onSubmit: handleSubmit,
              onBack: currentStep > 0 ? () => setCurrentStep(currentStep - 1) : undefined,
              currentStep,
              stepIndex: currentStep,
              setCurrentStep: (s: number) => setCurrentStep(s - 1),
            })}
          </div>
        )}

        {childrenArray.length === 0 && (
          <div aria-current="step" data-testid={`wizard-step-${currentStep + 1}`} style={{ width: '100%' }}>
            <Step
              initialValues={values}
              initialFormStatus={initialFormStatus}
              schema={schema.steps[currentStep]}
              onSubmit={handleSubmit}
              onBack={currentStep > 0 ? () => setCurrentStep(currentStep - 1) : undefined}
              currentStep={currentStep}
              stepIndex={currentStep}
              setCurrentStep={(s: number) => setCurrentStep(s - 1)}
            />
          </div>
        )}
      </Wizard.Root>
    </WizardProvider>
  )
}

export const MultiStepWizard = {
  Root,
  Step: (props: Partial<StepProps>) => <Step {...(props as StepProps)} />,
}
